import React, {useEffect, useState} from 'react';
import {flushSync} from 'react-dom';

import type {useHoverPopover} from 'lib/hooks/useHoverPopover';

import AnnotationEditor from '../AnnotationEditor/AnnotationEditor';
import {TABBABLE_SELECTOR} from '../shared/annotationFocusFallback';
import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import AnnotationNoteList from './AnnotationNoteList';
import {isSupplementAnchor} from '../engine/annotationAnchors';
import type {AnnotationNoteLine} from './annotationNoteLines';
import {selectIsNoteListed} from '../store/annotationSelectors';
import {useIsAnnotationModeAvailable} from '../mode/useIsAnnotationModeAvailable';
import {useRegionEdit} from '../store/useRegionEdit';

/** The note being edited; its editor hangs from the peek itself. */
interface PeekEdit {
  id: string;
  regionId: string;
  /** A whole-supplement mark paints as a border, which an underline cannot express. */
  allowUnderline: boolean;
}

interface AnnotationNotePeekProps {
  /** For the indicator to find the peek's controls. */
  id: string;
  /** Names the peek where it takes focus itself, having no controls to scroll it to. */
  label: string;
  line: AnnotationNoteLine;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentProps: ReturnType<typeof useHoverPopover>['contentProps'];
  onClose: () => void;
}

/** A peek's tab stops in order, itself first where it takes focus; none while it is not mounted. */
export const peekTabbables = (peek: HTMLElement | null): HTMLElement[] => {
  if (!peek) {
    return [];
  }
  const controls = Array.from(peek.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR));
  return peek.matches(TABBABLE_SELECTOR) ? [peek, ...controls] : controls;
};

/**
 * One line's notes, each editable in place. Mounted only while the peek is open,
 * so closing it discards the open editor rather than springing it on the next hover.
 * Escape closes it wherever focus is, unless an open editor or a modal dialog holds focus,
 * handing focus back to the indicator if it was inside; Tab moves through it as if it
 * followed the indicator, and shuts it on the way out. With no pencils, the peek is itself
 * a tab stop, so a keyboard can scroll a long note.
 */
export default function AnnotationNotePeek({
  id: peekId,
  label,
  line,
  triggerRef,
  contentProps,
  onClose
}: AnnotationNotePeekProps) {
  const [editingNote, openEditor, closeEditor] = useRegionEdit<PeekEdit>();
  const canEditAnnotations = useIsAnnotationModeAvailable();
  // State, not a ref: Popover reads `targetRef.current` during render.
  const [peekEl, setPeekEl] = useState<HTMLDivElement | null>(null);
  const isEditedNoteListed = useAnnotationModeStore((state) =>
    selectIsNoteListed(state, editingNote?.regionId, editingNote?.id)
  );
  const activeEdit = isEditedNoteListed ? editingNote : null;

  useEffect(() => {
    if (activeEdit) {
      return undefined;
    }
    // Capture, and stopped: the peek is the top layer, so this Escape is its alone —
    // not the mode exit's, nor a panel's or editor's beneath it.
    const closeOnEscape = (event: KeyboardEvent) => {
      const modal = document.activeElement?.closest('[aria-modal="true"]');
      if (
        event.key !== 'Escape' ||
        event.isComposing ||
        (modal && !modal.contains(triggerRef.current))
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (peekEl?.contains(document.activeElement)) {
        triggerRef.current?.focus({preventScroll: true});
      }
      onClose();
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [activeEdit, peekEl, triggerRef, onClose]);

  return (
    <div
      {...contentProps}
      id={peekId}
      ref={setPeekEl}
      {...(!canEditAnnotations && {role: 'region', 'aria-label': label, tabIndex: 0})}
      className='p-x1-half max-h-[320px] w-[260px] overflow-y-auto'
      data-testid='annotation-note-popover'
      onKeyDown={(event) => {
        if (event.key !== 'Tab') {
          return;
        }
        const tabbables = peekTabbables(event.currentTarget);
        const edge = event.shiftKey ? tabbables[0] : tabbables.at(-1);
        if (!edge || document.activeElement !== edge) {
          return;
        }
        triggerRef.current?.focus({preventScroll: true});
        if (event.shiftKey) {
          event.preventDefault();
          return;
        }
        // Unprevented, Tab carries on from the indicator. Shut first, or the portaled peek
        // is its next stop wherever nothing follows the indicator.
        flushSync(onClose);
      }}
    >
      <AnnotationNoteList
        annotations={line.annotations}
        editingNoteId={activeEdit?.id}
        onEditNote={
          canEditAnnotations
            ? ({id, anchor}) =>
                openEditor(
                  {id, regionId: anchor.regionId, allowUnderline: !isSupplementAnchor(anchor)},
                  anchor.regionId
                )
            : undefined
        }
      />
      {/* Inside the content, unlike the panel's: React routes enter/leave and
          focus through its own tree, so the hover bridge holds the peek open. */}
      {activeEdit && (
        <AnnotationEditor
          regionId={activeEdit.regionId}
          activeAnnotation={{id: activeEdit.id}}
          anchorEl={peekEl}
          allowUnderline={activeEdit.allowUnderline}
          focusNote
          onClose={() => {
            closeEditor();
            // Deferred past the commit, where the editor returns focus to the pencil. The
            // peek's list updates late, so a removed or emptied note is read from the store.
            setTimeout(() => {
              const {regionId, id} = activeEdit;
              if (!selectIsNoteListed(useAnnotationModeStore.getState(), regionId, id)) {
                triggerRef.current?.focus({preventScroll: true});
              }
            });
          }}
        />
      )}
    </div>
  );
}
